import React from 'react';

import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { PartnerBindingFieldset } from 'containers/PartnerBindingFieldset';
import { usePartnerBindingForm } from 'hooks/usePartnerBindingForm';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import { FieldsetLayoutComponent } from 'design/fieldsetLayout';
import { Partner } from 'types/state';
import { useServices } from 'hooks/useServices';
import { INPUT_PARTNER_CODE_MIN_LENGTH } from 'utils/constants';
import {
  ClientSubscriptionIsCancelledError,
  PartnerNotFoundError,
} from 'utils/errors';
import { FloatingLabelComponent } from 'design/floatingLabel';
import { TextComponent } from 'design/text/textComponents';
import { normalizeProfileLike } from 'utils/profileLikeUtils';
import { validatePartnerBindingFormValues } from 'utils/partnerBindingFormUtils';
import { SidebarModalSectionComponent } from 'design/sidebarModal';
import { FormControlComponent } from 'design/formControl';
import { MarginComponent } from 'design/margin';
import { ModalPressControlComponent } from 'design/modalPressControl';
import { ClientProductFieldsetComponent } from 'containers/ClientProductFieldsetComponent';
import { ClientProduct } from 'types/clientProductTypes';
import { useClientProductCalculation } from 'hooks/useClientProductCalculation';
import { useClientProductFormValues } from 'hooks/useClientProductFormValues';

type Props = {
  clientId: number;
  partner: null | Partner;
  clientProduct: ClientProduct;
  onCancel(): void;
  onSubmitDidSucceed(): void;
};

export const ClientDetailsPartnerBindingEditor = (props: Props) => {
  const {
    clientId,
    partner: initialPartner,
    clientProduct: {
      calculation: initialCalculation,
      defaultPartnerComission,
      m8ProductType,
    },
    onCancel,
    onSubmitDidSucceed,
  } = props;

  const intl = useIntlDictionary();

  const {
    clientDetails: { partnerBindingEditor: ownDictionary },
  } = intl;

  const {
    formValues: clientProductFormValues,
    setDiscountPercent,
    setPartnerPercent,
  } = useClientProductFormValues();

  React.useLayoutEffect(() => {
    setDiscountPercent(`${initialCalculation.discountPercent}`);
    setPartnerPercent(`${defaultPartnerComission}`);
  }, [
    defaultPartnerComission,
    initialCalculation.discountPercent,
    setDiscountPercent,
    setPartnerPercent,
  ]);

  const clientProductCalculation = useClientProductCalculation({
    clientId,
    m8ProductType: m8ProductType,
    initialCalculation: initialCalculation,
    formValues: clientProductFormValues,
  });

  const {
    getPartnerByCode,
    setClientPartnerBinding,
    submitClientProductForm,
  } = useServices();

  const [partner, setPartner] = React.useState<null | false | Partner>(null);

  const {
    values,
    violations,
    setPartnerCode,
    setViolations,
  } = usePartnerBindingForm();

  const { active: asyncActive, injectPromise } = useAsyncBoundary();

  React.useLayoutEffect(() => {
    if (initialPartner) {
      setPartner(initialPartner);
    }
  }, [initialPartner]);

  React.useLayoutEffect(() => {
    if (partner) {
      setPartnerCode(partner.code);
    }
  }, [partner, setPartnerCode]);

  const submitBatch = React.useCallback(() => {
    let promise: Promise<void> = Promise.resolve();

    if (partner && partner.id !== initialPartner?.id) {
      promise = promise.then(() => {
        return setClientPartnerBinding({
          clientId,
          partnerId: partner.id,
        });
      });
    }

    promise = promise.then(() => {
      return submitClientProductForm({
        clientId,
        formValues: clientProductFormValues,
        m8ProductType: m8ProductType,
      });
    });

    return promise;
  }, [
    clientId,
    clientProductFormValues,
    initialPartner?.id,
    m8ProductType,
    partner,
    setClientPartnerBinding,
    submitClientProductForm,
  ]);

  const submit = React.useCallback(() => {
    const nextViolations = validatePartnerBindingFormValues(values);

    if (nextViolations.length > 0) {
      Promise.resolve(nextViolations).then(setViolations);
    } else {
      const promise = submitBatch()
        .then(onSubmitDidSucceed)
        .catch((error) => {
          if (error instanceof ClientSubscriptionIsCancelledError) {
            setViolations([
              {
                name: 'BindingIsNotPossible',
              },
            ]);
          } else {
            throw error;
          }
        });

      injectPromise(promise, { shouldShowFallback: false });
    }
  }, [injectPromise, setViolations, submitBatch, onSubmitDidSucceed, values]);

  React.useEffect(() => {
    if (values.partnerCode.length === INPUT_PARTNER_CODE_MIN_LENGTH) {
      const promise = getPartnerByCode(values.partnerCode)
        .then(setPartner)
        .catch((error) => {
          if (error instanceof PartnerNotFoundError) {
            setPartner(false);
          } else {
            throw error;
          }
        });

      injectPromise(promise, { shouldShowFallback: false });
    } else {
      setPartner(null);
    }
  }, [getPartnerByCode, injectPromise, values.partnerCode]);

  return (
    <>
      <SidebarModalSectionComponent
        theme="head"
        content={
          <TextComponent size="mpp" weight="medium">
            {ownDictionary.title}
          </TextComponent>
        }
        actions={null}
        navigation={null}
      />
      <FormControlComponent onSubmit={submit}>
        <SidebarModalSectionComponent
          content={
            <>
              <FieldsetLayoutComponent>
                <PartnerBindingFieldset
                  values={values}
                  violations={violations}
                  disabled={asyncActive}
                  onPartnerCodeChange={setPartnerCode}
                />
                {partner !== null && (
                  <FloatingLabelComponent
                    lifted={!!values.partnerCode}
                    invalid={violations.length > 0}
                    titleText={
                      <TextComponent color="secondary" size="m">
                        {ownDictionary.fullNameLabel}
                      </TextComponent>
                    }
                    hintText={null}
                    withoutBorder
                  >
                    <TextComponent>
                      {!!partner
                        ? normalizeProfileLike(partner.profile).toFullName()
                        : ownDictionary.partnerNotFoundFallback}
                    </TextComponent>
                  </FloatingLabelComponent>
                )}
              </FieldsetLayoutComponent>
              {partner && (
                <ClientProductFieldsetComponent
                  values={clientProductFormValues}
                  calculation={clientProductCalculation}
                  m8ProductType={m8ProductType}
                  withPartnerPercent
                  onDiscountPercentChange={setDiscountPercent}
                  onPartnerPercentChange={setPartnerPercent}
                />
              )}
            </>
          }
          actions={
            <>
              <ModalPressControlComponent theme="primary" type="submit">
                {intl.saveChangesButton}
              </ModalPressControlComponent>
              <MarginComponent top={20} />
              <ModalPressControlComponent
                theme="secondary"
                type="button"
                onPress={onCancel}
              >
                {intl.cancelButton}
              </ModalPressControlComponent>
            </>
          }
        />
      </FormControlComponent>
    </>
  );
};
